// Direct-manipulation editing for the cable overlay. Selecting is always live;
// RESHAPING is a mode, because a click that silently bends a cable is a wrong
// number nobody asked for:
//   • click a SOURCE marker      -> pick it up; next click drops it (moveSource)
//   • click a route LINE or NODE -> select/isolate that circuit
//   • "Reshape" on that run      -> its GRAPH opens for editing
// Inside the mode, A CLICK NEVER ACTS — it reveals a sign, and the sign acts:
//   • click a NODE               -> it offers ✎ draw-from-here, and ✥ move-this
//                                   when the node is a joint he placed
//   • click a LENGTH             -> it goes red and offers ⊕ add-a-joint and
//                                   ✕ remove-it. Nothing is cut by the click
//   • ✎ then click another node  -> the two are joined, along x then along y
//   • ✥ or ⊕ then click the plan -> the joint is put down there
//   • Escape / click elsewhere   -> dismisses; nothing was written, so nothing
//                                   has to be put back
// Everything is hit-tested against the (derived) geometry using the plan-space
// coords the panel already gives us — so no per-element pointer wiring needed.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EditableCableTakeoff } from "../useEditableCableTakeoff";
import type { DeviceCategory } from "../deviceCategories";
import type { CableTakeoffGeometry } from "../../utils/fetchCableTakeoffPaths";
import type { WallKind, WallPoint } from "../planWalls";
import {
  connectGraphPath,
  edgeKeyOf,
  deleteGraphEdges,
  derivePaths,
  edgeMarksOf,
  footOnGraphEdge,
  graphFromPaths,
  mergeGraphNodes,
  moveGraphNode,
  snapDragPoint,
  orthogonalPath,
  splitGraphEdge,
  strandedEdgesOf,
  weldGraph,
  type CableReshapeView,
  type EdgeKey,
  type NodeKey,
  type PickedReshapeMark,
  type ReshapeSign,
  type RouteGraph,
} from "../routeReshape";

interface Pt {
  x: number;
  y: number;
}
/** Which devices a pick is FOR: the ones it could add, or the ones it could take. */
export type DevicePickPurpose = "add" | "remove";

/** One device the estimator has clicked while picking. */
export interface PickedDevice {
  destinationId: string;
  circuitId: string;
  position: Pt;
}

/**
 * Drawing a wall is a POLYLINE and not a two-click box: a wall turns corners,
 * and the tool that already draws corners here is the one the reshape uses. Each
 * click adds a corner; finishing writes it; Escape abandons it and nothing was
 * saved, because nothing is written until he finishes.
 */
export interface WallDraft {
  wallKind: WallKind;
  /** The corners so far, in plan space. Two of them make a wall. */
  points: Pt[];
  /**
   * Where the pointer is, so the plan can show where the next corner would
   * land. Without it he is clicking blind: the corners he has already placed
   * are drawn, and the length he is about to draw is not.
   */
  cursor: Pt | null;
}

export interface ZoneDraft {
  /** Board marker label the zone is drawn for. */
  sourceLabel: string;
  points: Pt[];
  cursor: Pt | null;
}

/**
 * Devices the edit has cut off from the board, held back at the moment he tries
 * to finish. He is not stopped from cutting — he cuts and then redraws — so the
 * question is only ever asked once, on the way out.
 */
/**
 * The ONE tool that is on, and the only thing the card's lit state reads.
 *
 * Reshape is a mode in this hook and Add/Remove are arms in the ask queue —
 * two stores, and while the card asked each of them separately, Remove and
 * Reshape could both be lit at once: "there should be no way this 2 buttons
 * active together". A store cannot be shared across the two hooks (an arm is
 * persisted queue state keyed to an ask; the mode is ephemeral and per-run),
 * but every one of those tools does its actual WORK here — Add and Remove are
 * `startDevicePick`, Reshape is `startShapeEdit` — so this hook can hold one
 * answer, and being one value it cannot report two.
 */
export interface ActiveCableTool {
  kind: "add" | "remove" | "shape";
  circuitId: string;
}

export interface ShapeExitQuestion {
  destinationIds: string[];
  positions: Pt[];
}

type Grabbed =
  | { kind: "source"; label: string }
  | { kind: "new_source"; name: string }
  | { kind: "main_board"; name: string; isFeedIn: boolean }
  | { kind: "feeder_for"; category: DeviceCategory }
  | { kind: "upstream_for"; toLabel: string }
  | { kind: "zone"; sourceLabel: string; points: Pt[] }
  | {
      kind: "pick";
      purpose: DevicePickPurpose;
      circuitId?: string;
      scope?: string[];
    }
  | { kind: "wall"; wallKind: WallKind; points: Pt[] }
  | null;

// Plan-space hit radii (generous — clicking "near" a thing grabs it). The two
// RESHAPE radii come from `routeReshape`, because the plan draws its targets at
// exactly those radii: a mark he can hover has to be the mark the click takes.
const SOURCE_HIT = 34;
const ZONE_MIN_CORNERS = 3;
const ZONE_CLOSE_HIT = 20;
const DEVICE_HIT = 30;
const NODE_SELECT_HIT = 26;
const LINE_SELECT_HIT = 22;

// Frozen: consumers key memos and effects on this identity.
const NO_DEVICES: PickedDevice[] = [];
Object.freeze(NO_DEVICES);

const d2 = (a: Pt, b: Pt) => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
};

// Squared distance from p to segment ab.
function segD2(p: Pt, a: Pt, b: Pt): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 === 0) return d2(p, a);
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  return d2(p, { x: a.x + t * abx, y: a.y + t * aby });
}

interface FoundNode {
  circuitId: string;
  destinationId: string;
}

interface NearestNodeArgs {
  byCircuit: CableTakeoffGeometry["byCircuit"];
  position: Pt;
}

type NearestNode = (args: NearestNodeArgs) => FoundNode | null;

const nearestNode: NearestNode = ({ byCircuit, position }) => {
  let best: FoundNode | null = null;
  let bestD = NODE_SELECT_HIT * NODE_SELECT_HIT;
  for (const [circuitId, state] of Object.entries(byCircuit)) {
    for (const path of state.paths) {
      for (const waypoint of path.waypoints) {
        const dd = d2(waypoint, position);
        if (dd >= bestD) continue;
        bestD = dd;
        best = { circuitId, destinationId: path.destinationId };
      }
    }
  }
  return best;
};

/** The device a click means: the end of a cable, not a bend in one. */
const scopedCircuits = (
  byCircuit: CableTakeoffGeometry["byCircuit"],
  scope: string[],
): CableTakeoffGeometry["byCircuit"] =>
  Object.fromEntries(
    scope
      .filter((circuitId) => byCircuit[circuitId])
      .map((circuitId) => [circuitId, byCircuit[circuitId]]),
  );

function nearestDevice(
  byCircuit: CableTakeoffGeometry["byCircuit"],
  pos: Pt,
): PickedDevice | null {
  let best: PickedDevice | null = null;
  let bestD = DEVICE_HIT * DEVICE_HIT;
  for (const [circuitId, state] of Object.entries(byCircuit)) {
    for (const path of state.paths) {
      const end = path.waypoints[path.waypoints.length - 1];
      if (!end) continue;
      const dd = d2(end, pos);
      if (dd >= bestD) continue;
      bestD = dd;
      best = {
        destinationId: path.destinationId,
        circuitId,
        position: { x: end.x, y: end.y },
      };
    }
  }
  return best;
}

const NO_MARK = (): void => undefined;

const EMPTY_VIEW: CableReshapeView = {
  edges: [],
  nodes: [],
  unreachable: [],
  sign: null,
  drawFrom: null,
  drawPoints: [],
  carrying: null,
  cursor: null,
  onMark: NO_MARK,
};
Object.freeze(EMPTY_VIEW);

export interface CableDirectEdit {
  onMapClick: (pos: Pt) => void;
  onMapMouseMove: (pos: Pt | null) => void;
  grabbed: {
    kind: "source" | "new_source" | "main_board" | "feeder_for" | "upstream_for";
    label: string;
    /** Picking the main board for a feed from another floor, not one on this plan. */
    isFeedIn?: boolean;
  } | null;
  cancel: () => void;
  /** Begin a source grab (used by the on-plan source toolbar's "Move"). */
  startMoveSource: (label: string) => void;
  /** Arm placement of a board the run never found; the next click drops it. */
  startAddSource: (name: string) => void;
  /** Next click names the main board: on a board it is that one; on bare plan a new board of that name is placed. */
  startPickMainBoard: (args: { name: string; isFeedIn: boolean }) => void;
  /** Next click on a board makes it feed this category too. */
  startPickFeederFor: (category: DeviceCategory) => void;
  /** Next click on a board makes it the one that feeds this board. */
  startPickUpstreamFor: (toLabel: string) => void;
  /** Arms the zone polygon for a board: corner by corner, closed on the first corner or by `finishZone`. */
  startZone: (sourceLabel: string) => void;
  zoneDraft: ZoneDraft | null;
  finishZone: () => void;
  /**
   * Arms wall drawing. Every click adds a corner, `finishWall` writes it and
   * Escape abandons it — nothing is saved until he finishes, so a stroke he did
   * not mean costs one Escape.
   */
  startWall: (wallKind: WallKind) => void;
  /** The corners so far, for the plan to draw while he is drawing. */
  wallDraft: WallDraft | null;
  /** Writes what he has drawn, if it is two corners or more. */
  finishWall: () => void;
  /**
   * Enter device picking: every device on the plan becomes clickable, and each
   * click adds or drops it. A rubber-band box asked him to enclose exactly the
   * right things in one gesture and nothing else — which on a floor plan is
   * mostly luck. Selecting is now the same as it is everywhere else.
   */
  /** `scope` limits what a click can take to these circuits' devices. */
  startDevicePick: (
    purpose: DevicePickPurpose,
    circuitId?: string,
    scope?: string[],
  ) => void;
  /** What he has picked so far, in the order he picked it. */
  pickedDevices: PickedDevice[];
  /** True while picking is on, whether or not anything is picked yet. */
  isPicking: boolean;
  /**
   * Every device the pick COULD take, so the plan can show him what is on
   * offer. Picking with nothing marked meant hunting for clickable dots.
   */
  candidateDevices: PickedDevice[];
  /** The held source drawn under the cursor until it is dropped. */
  ghost: { x: number; y: number; label: string } | null;
  /**
   * The circuit whose graph is open for editing, or null. Reshaping is a MODE
   * he enters on purpose: a plain click on a selected run used to grab the
   * nearest node and bend the cable under the cursor, so "i click on elements
   * it starts drawing some routes i do not mean for".
   */
  shapeEditCircuit: string | null;
  startShapeEdit: (circuitId: string) => void;
  /** Asks to finish. Cut devices hold it open until he answers about them. */
  endShapeEdit: () => void;
  /**
   * Which tool is on, at most one, ever. Switching is enforced where the state
   * lives rather than by asking two stores to agree, because that always
   * drifts back.
   */
  activeTool: ActiveCableTool | null;
  /**
   * Devices his edit has cut off from the board RIGHT NOW — before he has been
   * asked about them. Leaving the mode any way at all has to put that question,
   * including by reaching for another tool.
   */
  cutDeviceCount: number;
  /** Everything the plan draws for the run under edit. */
  reshapeView: CableReshapeView;
  /** Set only while finishing is waiting on an answer about cut devices. */
  shapeExit: ShapeExitQuestion | null;
  /** Yes: the cut devices go where struck devices go, and the mode closes. */
  confirmShapeExit: () => void;
  /** No: stay in, and reconnect them. */
  cancelShapeExit: () => void;
  /**
   * Escape backs out of the innermost thing: drawing, it abandons the stroke and
   * leaves him IN the mode; empty-handed, it asks to finish. One key doing both
   * at once cost him the mode for a slip of the hand.
   */
  escapeShapeEdit: () => void;
}

export function useCableDirectEdit({
  edit,
  geometry,
  onSelectCircuit,
  onSelectSource,
  selectedCircuit,
  routeEditingEnabled = true,
  onZoneDrawn,
  onWallDrawn,
}: {
  edit: EditableCableTakeoff;
  geometry: CableTakeoffGeometry | null;
  onSelectCircuit: (id: string | null) => void;
  /** Clicking a source SELECTS it (opens the toolbar) instead of grabbing. */
  onSelectSource?: (label: string) => void;
  /** Currently-selected circuit. A plain click SELECTS; reshaping needs the
   *  mode to be open on that same run. */
  selectedCircuit?: string | null;
  /** When false, only SOURCE markers stay interactive — route node dragging and
   *  circuit line selection are disabled (sources remain editable). */
  routeEditingEnabled?: boolean;
  /** A closed zone, for whoever persists it. */
  onZoneDrawn?: (sourceLabel: string, polygon: WallPoint[]) => void;
  /** A finished wall, for whoever persists it. */
  onWallDrawn?: (wallKind: WallKind, points: WallPoint[]) => void;
}): CableDirectEdit {
  const grabbedRef = useRef<Grabbed>(null);
  const [grabbed, setGrabbed] = useState<CableDirectEdit["grabbed"]>(null);
  const [ghost, setGhost] = useState<CableDirectEdit["ghost"]>(null);
  const [wallDraft, setWallDraft] = useState<WallDraft | null>(null);
  const [zoneDraft, setZoneDraft] = useState<ZoneDraft | null>(null);
  const [shapeEditCircuit, setShapeEditCircuit] = useState<string | null>(null);
  const [reshapeView, setReshapeView] = useState<CableReshapeView>(EMPTY_VIEW);
  const [shapeExit, setShapeExit] = useState<ShapeExitQuestion | null>(null);
  const [pickedDevices, setPickedDevices] = useState<PickedDevice[]>([]);
  const [pick, setPick] = useState<{
    purpose: DevicePickPurpose;
    circuitId?: string;
    scope?: string[];
  } | null>(null);

  // The working graph, and the stroke he is part-way through drawing. Both are
  // refs: a click reads them and writes them in the same tick, and a rendered
  // copy one frame behind would delete the wrong length.
  const graphRef = useRef<RouteGraph | null>(null);
  // The corner in his hand. A ref because a click reads it and writes it in the
  // same tick, and a rendered copy one frame behind would move the wrong one.
  // The one revealed sign, and the two things its buttons can arm. A click
  // never acts; it only sets `signRef`. The sign acts.
  const signRef = useRef<ReshapeSign | null>(null);
  const drawFromRef = useRef<NodeKey | null>(null);
  /** The bends he has placed since taking ✎. Written nowhere until he lands. */
  const drawPointsRef = useRef<Pt[]>([]);
  const carryRef = useRef<NodeKey | null>(null);
  // `startDevicePick` has to know the mode is open and be able to close it,
  // and both are declared below it — a ref is what lets one enforcement point
  // sit in each direction without either one being hoisted out of place.
  const shapeEditRef = useRef<string | null>(null);
  const endShapeEditRef = useRef<() => void>(() => undefined);
  // Where the cursor was when a mark reported a click. A mark says WHICH length
  // was hit; where along it he pointed still comes from the plan.
  const lastPosRef = useRef<Pt | null>(null);
  const selectedRef = useRef<EdgeKey[]>([]);
  // Read by the mode's own effect so it can build the graph without re-running
  // every time an edit re-derives the geometry under it.
  const geometryRef = useRef(geometry);
  geometryRef.current = geometry;

  // One key for one gesture, minted when a gesture starts. Every path it writes
  // coalesces under it, so a stroke is ONE step back however many cables of the
  // run were re-routed by it.
  const gestureSeqRef = useRef(0);
  const gestureKeyRef = useRef("reshape:0");

  const nextGestureKey = useCallback(() => {
    gestureSeqRef.current += 1;
    gestureKeyRef.current = `reshape:${gestureSeqRef.current}`;
    return gestureKeyRef.current;
  }, []);

  /**
   * What the plan draws. Built from the graph, never re-derived on the plan:
   * the marks he can click and the graph the click acts on have to be one thing.
   */
  // Held in a ref and never re-created: the view is published on every mouse
  // move, and a fresh arrow each time would re-render the whole overlay.
  const onMarkRef = useRef<(pick: PickedReshapeMark) => void>(NO_MARK);

  const publish = useCallback((graph: RouteGraph | null) => {
    if (!graph) {
      setReshapeView(EMPTY_VIEW);
      return;
    }
    const derived = derivePaths(graph);
    const picked = new Set(selectedRef.current);
    setReshapeView({
      edges: edgeMarksOf({ graph, usedEdgeKeys: derived.usedEdgeKeys }).map(
        (mark) => ({ ...mark, isSelected: picked.has(mark.key) }),
      ),
      nodes: Object.entries(graph.points).map(([key, point]) => ({
        key,
        x: point.x,
        y: point.y,
        // The board and every device are landable but never draggable: they are
        // where the survey put them, not shapes he drew.
        isDraggable:
          key !== graph.boardKey &&
          !Object.values(graph.deviceKeys).includes(key),
      })),
      unreachable: derived.unreachable
        .map((destinationId) => graph.points[graph.deviceKeys[destinationId]])
        .filter((point): point is Pt => Boolean(point))
        .map((point) => ({ ...point })),
      sign: signRef.current,
      drawFrom: drawFromRef.current,
      drawPoints: drawPointsRef.current,
      carrying: carryRef.current,
      cursor:
        drawFromRef.current || carryRef.current ? lastPosRef.current : null,
      onMark: onMarkRef.current,
    });
  }, []);

  /**
   * Push the graph back out as one polyline per destination — the shape the
   * document stores and the metres are measured from. It is safe to rewrite
   * them wholesale because `circuitMetres` counts each distinct segment once
   * across a circuit, so a circuit's length is the length of its EDGE SET
   * however the paths through it are split up.
   */
  const project = useCallback(
    (graph: RouteGraph, coalesceKey: string) => {
      const derived = derivePaths(graph);
      // The paths AND the lengths no path runs over, in ONE write under one
      // coalesce key — so a drawn cable survives Done editing and a reload, and
      // costs exactly one step back like every other edit. Leaving the second
      // half out is what threw his cable away: "i clicked and made doted new
      // route, but its not saved even after done, why?"
      edit.setRoutePaths(
        Object.entries(derived.byDestination)
          .filter(([, waypoints]) => waypoints.length > 0)
          .map(([destinationId, waypoints]) => ({ destinationId, waypoints })),
        coalesceKey,
        shapeEditRef.current
          ? {
              circuitId: shapeEditRef.current,
              edges: strandedEdgesOf({
                graph,
                usedEdgeKeys: derived.usedEdgeKeys,
              }),
            }
          : undefined,
      );
      return derived;
    },
    [edit],
  );

  const commitGraph = useCallback(
    (graph: RouteGraph, coalesceKey?: string) => {
      graphRef.current = graph;
      project(graph, coalesceKey ?? nextGestureKey());
      publish(graph);
    },
    [project, publish, nextGestureKey],
  );

  const drop = useCallback(() => {
    setGhost(null);
    setPick(null);
    setPickedDevices([]);
    setWallDraft(null);
    setZoneDraft(null);
    grabbedRef.current = null;
    setGrabbed(null);
  }, []);

  const setSelected = useCallback((keys: EdgeKey[]) => {
    selectedRef.current = keys;
  }, []);

  // Nothing is written while a node is armed, so letting go costs nothing to
  // put back: there is no half-made cable and no node left where the cursor
  // happened to be.
  // Dismiss whatever is revealed or armed. Nothing has been written, so there
  // is nothing to put back.
  const disarm = useCallback(() => {
    signRef.current = null;
    drawFromRef.current = null;
    drawPointsRef.current = [];
    carryRef.current = null;
    publish(graphRef.current);
  }, [publish]);

  const cancel = useCallback(() => {
    disarm();
    drop();
  }, [drop, disarm]);

  /**
   * A mark on the plan reported a click. The plan knows the zoom, so the reach
   * of every mark is whatever is drawn on screen — which is what makes zooming
   * in the remedy for two corners on top of each other, and what the old
   * plan-point radii could never do.
   */
  const onMark = useCallback(
    (pick: PickedReshapeMark) => {
      const graph = graphRef.current;
      if (!graph) return;

      /**
       * ---- A joint in his hand, dropped ON something. ---------------------
       *
       * This is what makes two pieces ONE route. Connectivity is node identity,
       * so a joint that merely lands at the same COORDINATE as a length is
       * touching it and joined to nothing — *"i take the joint and moved
       * forward to the edge but its not recognizing that they should become one
       * route"*. Dropping it on a length splits that length there; dropping it
       * on a node merges the two.
       *
       * The reach is the target he clicked, which the PLAN hit-tested in screen
       * space — so it is the same reach at every zoom, and no plan-space
       * constant is invented here to shrink against the drawing.
       */
      if (carryRef.current && (pick.kind === "node" || pick.kind === "edge")) {
        const held = carryRef.current;
        carryRef.current = null;
        if (pick.kind === "node") {
          commitGraph(
            weldGraph(
              mergeGraphNodes({ graph, from: held, into: pick.nodeKey }),
            ),
            gestureKeyRef.current,
          );
          return;
        }
        const foot = footOnGraphEdge({
          graph,
          edgeKey: pick.edgeKey,
          at: lastPosRef.current ?? graph.points[held],
        });
        if (!foot) {
          publish(graph);
          return;
        }
        const split = splitGraphEdge({
          graph,
          edgeKey: pick.edgeKey,
          at: foot,
        });
        commitGraph(
          weldGraph(
            mergeGraphNodes({
              graph: split.graph,
              from: held,
              into: split.key,
            }),
          ),
          gestureKeyRef.current,
        );
        return;
      }

      // ---- A stroke finishing ON a length: it taps into it there. ---------
      if (drawFromRef.current && pick.kind === "edge") {
        const from = drawFromRef.current;
        const bends = drawPointsRef.current;
        drawFromRef.current = null;
        drawPointsRef.current = [];
        signRef.current = null;
        const a = graph.points[from];
        const foot = footOnGraphEdge({
          graph,
          edgeKey: pick.edgeKey,
          at: lastPosRef.current ?? graph.points[from],
        });
        if (!a || !foot) {
          publish(graph);
          return;
        }
        commitGraph(
          connectGraphPath({
            graph,
            points: orthogonalPath([a, ...bends, foot]),
          }),
        );
        return;
      }

      // ---- A click on the thing itself. It NEVER acts; it reveals. --------
      if (pick.kind === "node") {
        // Mid-draw a node click is the second half of the connection, which is
        // the one place a node click does act — and he asked for it to.
        const from = drawFromRef.current;
        if (from) {
          const bends = drawPointsRef.current;
          drawFromRef.current = null;
          drawPointsRef.current = [];
          signRef.current = null;
          const a = graph.points[from];
          const b = graph.points[pick.nodeKey];
          // The same node twice lays no cable, and neither does a second cable
          // straight over an existing one — but only when he drew it STRAIGHT.
          // Once he has bent it round, it is a different route between the same
          // two points and he is entitled to it.
          if (
            !a ||
            !b ||
            from === pick.nodeKey ||
            (!bends.length && graph.edges[edgeKeyOf(from, pick.nodeKey)])
          ) {
            publish(graph);
            return;
          }
          commitGraph(
            connectGraphPath({
              graph,
              points: orthogonalPath([a, ...bends, b]),
            }),
          );
          return;
        }
        signRef.current = { kind: "node", key: pick.nodeKey };
        publish(graph);
        return;
      }
      // A JOINT IS A HANDLE. Clicking it puts it in his hand — no menu, no
      // sign, nothing written. This is the interactivity he had and asked for
      // twice; wrapping it in a ✥ sign is what "why you removed it?" was about.
      // It writes nothing, so the rule the signs exist for — no click may WRITE
      // — still holds.
      if (pick.kind === "grabJoint") {
        signRef.current = null;
        drawFromRef.current = null;
        carryRef.current = pick.nodeKey;
        nextGestureKey();
        publish(graph);
        return;
      }
      if (pick.kind === "edge") {
        // It goes red and offers its signs. It is NOT cut.
        signRef.current = { kind: "edge", key: pick.edgeKey };
        setSelected([pick.edgeKey]);
        publish(graph);
        return;
      }

      // ---- The signs. These are the only things that act. ----------------
      if (pick.kind === "signDraw") {
        signRef.current = null;
        drawFromRef.current = pick.nodeKey;
        publish(graph);
        return;
      }
      if (pick.kind === "signRemove") {
        signRef.current = null;
        setSelected([]);
        commitGraph(deleteGraphEdges({ graph, edgeKeys: [pick.edgeKey] }));
        return;
      }
      const mark = edgeMarksOf({ graph, usedEdgeKeys: [] }).find(
        (candidate) => candidate.key === pick.edgeKey,
      );
      if (!mark) return;
      // Born on the length and handed straight over, so pulling it into an L is
      // one gesture and not two.
      const split = splitGraphEdge({
        graph,
        edgeKey: pick.edgeKey,
        at: { x: mark.x, y: mark.y },
      });
      signRef.current = null;
      setSelected([]);
      carryRef.current = split.key;
      commitGraph(split.graph);
    },
    [commitGraph, nextGestureKey, publish, setSelected],
  );
  onMarkRef.current = onMark;

  const onMapClick = useCallback(
    (pos: Pt) => {
      if (!geometry) return;

      const g = grabbedRef.current;
      if (g?.kind === "wall") {
        // Corner by corner, and nothing is written yet. A wall is only saved
        // when he says it is finished, so a mis-click is one Escape.
        const next = [...g.points, pos];
        grabbedRef.current = { ...g, points: next };
        setWallDraft({ wallKind: g.wallKind, points: next, cursor: pos });
        return;
      }
      if (g?.kind === "pick") {
        const device = nearestDevice(
          g.scope ? scopedCircuits(geometry.byCircuit, g.scope) : geometry.byCircuit,
          pos,
        );
        if (!device) return;
        setPickedDevices((picked) =>
          picked.some((p) => p.destinationId === device.destinationId)
            ? picked.filter((p) => p.destinationId !== device.destinationId)
            : [...picked, device],
        );
        return;
      }
      if (g?.kind === "zone") {
        const first = g.points[0];
        if (
          first &&
          g.points.length >= ZONE_MIN_CORNERS &&
          Math.hypot(pos.x - first.x, pos.y - first.y) <= ZONE_CLOSE_HIT
        ) {
          onZoneDrawn?.(g.sourceLabel, g.points.map((point) => ({ x: point.x, y: point.y })));
          drop();
          return;
        }
        const next = [...g.points, pos];
        grabbedRef.current = { ...g, points: next };
        setZoneDraft({ sourceLabel: g.sourceLabel, points: next, cursor: pos });
        return;
      }

      if (g?.kind === "feeder_for") {
        const board = geometry.sources.find((source) => d2(source, pos) < SOURCE_HIT * SOURCE_HIT);
        if (!board) return;
        const current = edit.feedsByBoard[board.label]?.feed ?? { categories: [], excludedTypes: [] };
        if (!current.categories.includes(g.category)) {
          edit.setBoardFeeds(board.label, {
            ...current,
            categories: [...current.categories, g.category],
          });
        }
        drop();
        return;
      }
      if (g?.kind === "upstream_for") {
        const board = geometry.sources.find((source) => d2(source, pos) < SOURCE_HIT * SOURCE_HIT);
        // A board cannot feed itself; a miss leaves the pick armed so he can try again.
        if (!board || board.label === g.toLabel) return;
        edit.setFeederFrom(g.toLabel, board.label);
        drop();
        return;
      }
      if (g?.kind === "main_board") {
        const board = geometry.sources.find((source) => d2(source, pos) < SOURCE_HIT * SOURCE_HIT);
        if (board) {
          edit.setMainBoard({ label: board.label, by: "picked" });
        } else {
          const label = edit.addSource(g.name, pos);
          edit.setMainBoard({ label, by: g.isFeedIn ? "feedIn" : "picked" });
        }
        drop();
        return;
      }

      // Holding a board -> drop it here.
      if (g) {
        if (g.kind === "source") edit.moveSource(g.label, pos.x, pos.y);
        else edit.addSource(g.name, pos);
        drop();
        return;
      }

      const graph = graphRef.current;

      // A joint is in his hand from ✥: this puts it down. Nothing else on the
      // bare plan writes anything — a missed click while drawing used to nudge
      // a node, and under this design a miss is just a miss.
      if (graph && carryRef.current) {
        const key = carryRef.current;
        carryRef.current = null;
        commitGraph(
          moveGraphNode({
            graph,
            key,
            to: snapDragPoint({ graph, key, position: pos }),
          }),
          gestureKeyRef.current,
        );
        return;
      }
      /**
       * MID-STROKE, BARE PLAN IS A BEND. "this should be arbitrary not just
       * L-shape" — two clicks still give the plain L, and every click he adds
       * in between turns the cable somewhere he chose.
       *
       * This is not the pen coming back. The pen failed because a near miss
       * placed a stray corner and the stroke had no end. Now the nodes are
       * ringed the moment he starts drawing and their targets are Ø60, so a
       * click on bare plan is plain intent rather than a miss; landing on a
       * node ends it; and — the part that actually makes it safe — **nothing
       * here is written**. The bends live in a ref until he lands, so a stray
       * click costs one Escape and leaves the drawing exactly as it was. The
       * pen wrote each corner as it went.
       */
      if (graph && drawFromRef.current) {
        drawPointsRef.current = [...drawPointsRef.current, pos];
        publish(graph);
        return;
      }
      // Clicking anywhere else dismisses whatever was revealed or armed.
      if (graph && signRef.current) {
        disarm();
        return;
      }

      /**
       * THE MODE OWNS THE PLAN. While a run is open for reshaping, a bare click
       * goes no further — it may not select another run, and it may not pull
       * the toolbar onto a board.
       *
       * The graph is built from ONE circuit's paths, so a cable to a second run
       * cannot be drawn: every destination's polyline starts at its own run's
       * board, and there is nowhere in `routeOverrides` for a cable between two
       * of them to live. It is a real boundary of the model, not a missing
       * feature — moving a device onto the other run is the operation that
       * exists, and it is on the device's own card.
       *
       * What must not happen is what did: clicking the other run selected it,
       * which made `shapeEditCircuit !== selectedCircuit`, which LEFT THE MODE.
       * He reached for a second run and the whole surface quietly closed. The
       * card says "this edits one run only" for the whole time the mode is
       * open, so the answer is on screen before he tries rather than after.
       */
      if (shapeEditRef.current) return;

      // A struck device's red ✕ ghost used to be a live undo button: a click
      // within REMOVED_HIT = 30 PLAN POINTS put the device back, and putting it
      // back redraws its cable. Thirty plan points is ~125 screen pixels on
      // project 153, so a wide patch around every device he had just struck
      // silently restored routes — "i click on remove object it creates routes,
      // even ununderstandable routes there".
      //
      // Nothing that changes route geometry may happen on a bare click: "when i
      // havent selected reshape there should be no any operation on route
      // changing". Restoring is on the card, in the removed-devices list, where
      // it is named and asked for.

      // 1) nearest source marker
      let src: string | null = null;
      let srcD = SOURCE_HIT * SOURCE_HIT;
      for (const s of geometry.sources) {
        const dd = d2(s, pos);
        if (dd < srcD) {
          srcD = dd;
          src = s.label;
        }
      }
      if (src) {
        // Select (open the toolbar near it) instead of grabbing immediately.
        onSelectSource?.(src);
        return;
      }

      // Route editing disabled -> plan is source-only; ignore node/line clicks.
      if (!routeEditingEnabled) return;

      // 3) any other route node -> select that circuit, never grab it. The
      //    device end counts here: clicking a symbol to isolate the run it
      //    hangs on moves nothing, so there is nothing for it to misreport.
      const found = nearestNode({
        byCircuit: geometry.byCircuit,
        position: pos,
      });
      if (found) {
        onSelectCircuit(found.circuitId);
        return;
      }

      // 4) nearest route line -> select that circuit
      let lineCid: string | null = null;
      let lineD = LINE_SELECT_HIT * LINE_SELECT_HIT;
      for (const [cid, state] of Object.entries(geometry.byCircuit)) {
        for (const path of state.paths) {
          for (let i = 0; i < path.waypoints.length - 1; i++) {
            const dd = segD2(pos, path.waypoints[i], path.waypoints[i + 1]);
            if (dd < lineD) {
              lineD = dd;
              lineCid = cid;
            }
          }
        }
      }
      onSelectCircuit(lineCid);
    },
    [
      geometry,
      edit,
      drop,
      onSelectCircuit,
      onSelectSource,
      routeEditingEnabled,
      onZoneDrawn,
      commitGraph,
      disarm,
      publish,
    ],
  );

  const startMoveSource = useCallback((label: string) => {
    grabbedRef.current = { kind: "source", label };
    setGrabbed({ kind: "source", label });
  }, []);

  const startZone = useCallback((sourceLabel: string) => {
    grabbedRef.current = { kind: "zone", sourceLabel, points: [] };
    setZoneDraft({ sourceLabel, points: [], cursor: null });
    setWallDraft(null);
    setGrabbed(null);
  }, []);

  const finishZone = useCallback(() => {
    const g = grabbedRef.current;
    if (g?.kind !== "zone") return;
    if (g.points.length >= ZONE_MIN_CORNERS) {
      onZoneDrawn?.(g.sourceLabel, g.points.map((point) => ({ x: point.x, y: point.y })));
    }
    drop();
  }, [drop, onZoneDrawn]);

  const startWall = useCallback((wallKind: WallKind) => {
    grabbedRef.current = { kind: "wall", wallKind, points: [] };
    // The cursor is unknown until he moves: the plan marks nothing rather than
    // marking the last place the mouse happened to be.
    setWallDraft({ wallKind, points: [], cursor: null });
    setZoneDraft(null);
    setGrabbed(null);
  }, []);

  const finishWall = useCallback(() => {
    const g = grabbedRef.current;
    if (g?.kind !== "wall") return;
    // One click is a point, not a wall. Two is the shortest thing that can be
    // in the way of anything.
    if (g.points.length >= 2) {
      onWallDrawn?.(
        g.wallKind,
        g.points.map((point): WallPoint => ({ x: point.x, y: point.y })),
      );
    }
    drop();
  }, [drop, onWallDrawn]);

  // What the pick is offering him. Adding shows what is NOT on this run yet;
  // removing shows what IS. Marking everything would be the same as marking
  // nothing — he still would not know which dots the click means.
  const candidateDevices = useMemo<PickedDevice[]>(() => {
    if (!pick || !geometry) return NO_DEVICES;
    const found: PickedDevice[] = [];
    const byCircuit = pick.scope
      ? scopedCircuits(geometry.byCircuit, pick.scope)
      : geometry.byCircuit;
    for (const [circuitId, state] of Object.entries(byCircuit)) {
      const isOwn = circuitId === pick.circuitId;
      if (pick.purpose === "add" ? isOwn : !isOwn) continue;
      for (const path of state.paths) {
        const end = path.waypoints[path.waypoints.length - 1];
        if (!end) continue;
        found.push({
          destinationId: path.destinationId,
          circuitId,
          position: { x: end.x, y: end.y },
        });
      }
    }
    return found;
  }, [pick, geometry]);

  /**
   * Reaching for another tool leaves shape edit — and leaving it costs the same
   * question as any other way out. Devices his edit cut off are NOT quietly
   * abandoned here: the question goes up and the tool does not arm, so he
   * answers it and reaches again.
   */
  const startDevicePick = useCallback(
    (purpose: DevicePickPurpose, circuitId?: string, scope?: string[]) => {
      if (shapeEditRef.current) {
        endShapeEditRef.current();
        if (shapeEditRef.current) return;
      }
      grabbedRef.current = { kind: "pick", purpose, circuitId, scope };
      setPick({ purpose, circuitId, scope });
      setPickedDevices([]);
      setGrabbed(null);
    },
    [],
  );

  // And the other way round: entering the mode puts down whatever was armed.
  // One of these two enforcements without the other is how they drifted apart.
  const startShapeEdit = useCallback((circuitId: string) => {
    grabbedRef.current = null;
    setPick(null);
    setPickedDevices([]);
    setGrabbed(null);
    shapeEditRef.current = circuitId;
    setShapeEditCircuit(circuitId);
  }, []);

  shapeEditRef.current = shapeEditCircuit;

  const leaveShapeEdit = useCallback(() => {
    signRef.current = null;
    drawFromRef.current = null;
    drawPointsRef.current = [];
    carryRef.current = null;
    graphRef.current = null;
    selectedRef.current = [];
    setShapeExit(null);
    setReshapeView(EMPTY_VIEW);
    shapeEditRef.current = null;
    setShapeEditCircuit(null);
  }, []);

  /**
   * He is never stopped from cutting: he cuts a length and THEN draws its
   * replacement, so a cascade at the moment of deletion would leave the redraw
   * with nothing to reconnect. The question is asked once, here, on the way out.
   */
  const endShapeEdit = useCallback(() => {
    const graph = graphRef.current;
    disarm();
    if (!graph) {
      leaveShapeEdit();
      return;
    }
    const cut = derivePaths(graph).unreachable;
    if (!cut.length) {
      leaveShapeEdit();
      return;
    }
    setShapeExit({
      destinationIds: cut,
      positions: cut
        .map((destinationId) => graph.points[graph.deviceKeys[destinationId]])
        .filter((point): point is Pt => Boolean(point))
        .map((point) => ({ ...point })),
    });
  }, [disarm, leaveShapeEdit]);

  // ONE write, so the whole answer is one removal group he can put back with one
  // control and one step back on the undo stack.
  const confirmShapeExit = useCallback(() => {
    if (shapeExit?.destinationIds.length) {
      edit.removeDevices(shapeExit.destinationIds);
    }
    leaveShapeEdit();
  }, [edit, shapeExit, leaveShapeEdit]);

  const cancelShapeExit = useCallback(() => setShapeExit(null), []);

  endShapeEditRef.current = endShapeEdit;

  // Read from the graph, not from `shapeExit`: he has to be stopped from
  // switching away BEFORE the question has been raised, which is the only
  // moment it could otherwise be skipped.
  const cutDeviceCount = useMemo(
    () => reshapeView.unreachable.length,
    [reshapeView],
  );

  // One value, so it cannot say two tools are on. Shape leads: it is a mode he
  // is standing in, and `startDevicePick` refuses to arm while it is open.
  const activeTool = useMemo<ActiveCableTool | null>(() => {
    if (shapeEditCircuit) return { kind: "shape", circuitId: shapeEditCircuit };
    if (pick?.circuitId) {
      return { kind: pick.purpose, circuitId: pick.circuitId };
    }
    return null;
  }, [shapeEditCircuit, pick]);

  const escapeShapeEdit = useCallback(() => {
    if (signRef.current || drawFromRef.current || carryRef.current) {
      disarm();
      return;
    }
    if (shapeExit) {
      setShapeExit(null);
      return;
    }
    endShapeEdit();
  }, [disarm, endShapeEdit, shapeExit]);

  const startAddSource = useCallback((name: string) => {
    grabbedRef.current = { kind: "new_source", name };
    setGrabbed({ kind: "new_source", label: name });
  }, []);

  const startPickFeederFor = useCallback((category: DeviceCategory) => {
    grabbedRef.current = { kind: "feeder_for", category };
    setGrabbed({ kind: "feeder_for", label: category });
  }, []);

  const startPickUpstreamFor = useCallback((toLabel: string) => {
    grabbedRef.current = { kind: "upstream_for", toLabel };
    setGrabbed({ kind: "upstream_for", label: toLabel });
  }, []);

  const startPickMainBoard = useCallback(({ name, isFeedIn }: { name: string; isFeedIn: boolean }) => {
    grabbedRef.current = { kind: "main_board", name, isFeedIn };
    setGrabbed({ kind: "main_board", label: name, isFeedIn });
  }, []);

  // The graph is built ONCE, when the mode opens on a run. Rebuilding it as the
  // geometry re-derives would read back our own projection and lose every length
  // he has drawn that no device needs yet.
  useEffect(() => {
    if (!shapeEditCircuit) {
      graphRef.current = null;
      signRef.current = null;
      drawFromRef.current = null;
      drawPointsRef.current = [];
      carryRef.current = null;
      setReshapeView(EMPTY_VIEW);
      return;
    }
    const state = geometryRef.current?.byCircuit[shapeEditCircuit];
    const paths = state?.paths ?? [];
    let graph = graphFromPaths(
      paths.map((path) => ({
        destinationId: path.destinationId,
        waypoints: path.waypoints.map((w) => ({ x: w.x, y: w.y })),
      })),
    );
    // The lengths he drew that carry no cable. The polylines above reproduce
    // every USED length, these are exactly the unused ones, and their union is
    // the graph he left — so what he sees on re-opening is what he drew, and
    // re-deriving reproduces the same routes because it is the same graph.
    for (const edge of state?.strandedEdges ?? []) {
      graph = connectGraphPath({
        graph,
        points: [
          { x: edge.a.x, y: edge.a.y },
          { x: edge.b.x, y: edge.b.y },
        ],
      });
    }
    graphRef.current = graph;
    signRef.current = null;
    drawFromRef.current = null;
    drawPointsRef.current = [];
    carryRef.current = null;
    selectedRef.current = [];
    publish(graph);
  }, [shapeEditCircuit, publish]);

  // Selecting another run leaves the mode behind. Without this the mode
  // outlived the circuit it belonged to, and a click on the newly selected run
  // started drawing on a graph that was no longer on screen.
  useEffect(() => {
    if (shapeEditCircuit && shapeEditCircuit !== selectedCircuit) {
      leaveShapeEdit();
    }
  }, [shapeEditCircuit, selectedCircuit, leaveShapeEdit]);

  const onMapMouseMove = useCallback(
    (pos: Pt | null) => {
      if (pos) lastPosRef.current = pos;
      // A node is armed: the plan previews from the cursor, and NOTHING is
      // written until he clicks. The corner used to follow the pointer and be
      // written on every move, which is most of what he could not read.
      if (graphRef.current && (drawFromRef.current || carryRef.current)) {
        publish(graphRef.current);
        return;
      }

      const g = grabbedRef.current;
      if (!g || !pos) return;
      if (g.kind === "wall") {
        // The rubber band the plan draws from the last corner. Nothing is
        // written on a move — this is only where the next click would put it.
        setWallDraft({ wallKind: g.wallKind, points: g.points, cursor: pos });
        return;
      }
      if (g.kind === "zone") {
        setZoneDraft({ sourceLabel: g.sourceLabel, points: g.points, cursor: pos });
        return;
      }
      // A source follows the cursor as a ghost rather than by recomputing every
      // circuit on each mouse move; it commits on click.
      if (g.kind === "source" || g.kind === "new_source") {
        setGhost({
          x: pos.x,
          y: pos.y,
          label: g.kind === "source" ? g.label : g.name,
        });
      }
    },
    [publish],
  );

  return {
    onMapClick,
    onMapMouseMove,
    grabbed,
    cancel,
    startAddSource,
    startPickMainBoard,
    startPickFeederFor,
    startPickUpstreamFor,
    startZone,
    zoneDraft,
    finishZone,
    startWall,
    wallDraft,
    finishWall,
    startMoveSource,
    startDevicePick,
    pickedDevices,
    isPicking: pick !== null,
    candidateDevices,
    ghost,
    shapeEditCircuit,
    startShapeEdit,
    endShapeEdit,
    activeTool,
    cutDeviceCount,
    reshapeView,
    shapeExit,
    confirmShapeExit,
    cancelShapeExit,
    escapeShapeEdit,
  };
}
